import { log } from "@/core/logger";
import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { IReportAPI } from "./reportApi";
import type { Report, ReportConfiguration, ReportTemplate } from "@/types/Report";

type ReportFilters = {
  status?: string;
  templateId?: string;
  limit?: number;
  skip?: number;
};

class RemoteReportAPI implements IReportAPI {
  clearCache(): void {
    // No caching implemented in remote API
    return;
  }

  async fetchTemplates(): Promise<ReportTemplate[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/reports/templates`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result = await response.json();

      // Ensure we return an array even if data is undefined
      if (!result.data) {
        log("remoteReportApi", "API returned no data property", result);
        return [];
      }

      return result.data as ReportTemplate[];
    } catch (error) {
      log("remoteReportApi", "Failed to fetch report templates", error);
      throw error;
    }
  }

  async fetchReportById(reportId: string): Promise<Report> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/reports/${reportId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result = await response.json();

      // Check if the response has a data property (consistent with other endpoints)
      if (result.data) {
        return result.data as Report;
      }

      return result as Report;
    } catch (error) {
      log("remoteReportApi", "Failed to fetch report by ID", error);
      throw error;
    }
  }

  async fetchReports(filters: ReportFilters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.templateId) params.append("templateId", filters.templateId);
      if (filters.limit) params.append("limit", filters.limit.toString());
      if (filters.skip) params.append("skip", filters.skip.toString());

      const response = await fetchWithAuth(`${API_BASE_URL}/api/reports?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result = await response.json();
      return {
        data: result.data as Report[],
        pagination: result.pagination,
      };
    } catch (error) {
      log("remoteReportApi", "Failed to fetch reports", error);
      throw error;
    }
  }

  async createReport(templateId: string, configuration: ReportConfiguration): Promise<Report> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ templateId, configuration }),
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result = await response.json();

      // Check if the response has a data property (consistent with other endpoints)
      if (result.data) {
        return result.data as Report;
      }

      return result as Report;
    } catch (error) {
      log("remoteReportApi", "Failed to create report", error);
      throw error;
    }
  }

  async deleteReport(reportId: string): Promise<void> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/reports/${reportId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      return;
    } catch (error) {
      log("remoteReportApi", "Failed to delete report", error);
      throw error;
    }
  }
}

export default RemoteReportAPI;
